import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface PromoCode { id: string; code: string; discount_percent: number | null; discount_fixed: number | null; is_active: boolean; max_uses: number | null; current_uses: number; expires_at: string | null; }

const emptyForm = { code: '', discount_percent: 0, discount_fixed: 0, is_active: true, max_uses: '', expires_at: '' };

const AdminPromoCodes = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const { data } = await supabase.from('promo_codes').select('*').order('created_at', { ascending: false }) as any;
    setItems(data || []);
    setLoading(false);
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p: PromoCode) => {
    setEditing(p);
    setForm({ code: p.code, discount_percent: p.discount_percent || 0, discount_fixed: p.discount_fixed || 0, is_active: p.is_active, max_uses: p.max_uses?.toString() || '', expires_at: p.expires_at ? p.expires_at.split('T')[0] : '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload: any = {
      code: form.code.toUpperCase(), discount_percent: form.discount_percent || 0, discount_fixed: form.discount_fixed || 0,
      is_active: form.is_active, max_uses: form.max_uses ? parseInt(form.max_uses) : null, expires_at: form.expires_at || null,
    };
    try {
      if (editing) {
        const { error } = await supabase.from('promo_codes').update(payload).eq('id', editing.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('promo_codes').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: editing ? 'Updated' : 'Created' });
      setDialogOpen(false);
      fetchAll();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('promo_codes').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  const isExpired = (date: string | null) => date && new Date(date) < new Date();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Promo Codes</h1>
            <p className="text-muted-foreground">Manage discount codes for shuttle bookings</p>
          </div>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Add Promo Code</Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Uses</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">No promo codes found.</TableCell></TableRow>
                ) : items.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono font-bold">{p.code}</TableCell>
                    <TableCell>
                      {p.discount_percent ? `${p.discount_percent}%` : ''}
                      {p.discount_percent && p.discount_fixed ? ' + ' : ''}
                      {p.discount_fixed ? `$${p.discount_fixed}` : ''}
                    </TableCell>
                    <TableCell>{p.current_uses}{p.max_uses ? `/${p.max_uses}` : ''}</TableCell>
                    <TableCell>
                      {p.expires_at ? (
                        <span className={isExpired(p.expires_at) ? 'text-destructive' : ''}>
                          {new Date(p.expires_at).toLocaleDateString()}
                          {isExpired(p.expires_at) && ' (expired)'}
                        </span>
                      ) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {p.is_active ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete code {p.code}?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(p.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit Promo Code' : 'Add Promo Code'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="e.g. SUMMER20" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Discount %</Label><Input type="number" value={form.discount_percent} onChange={e => setForm({...form, discount_percent: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Discount Fixed ($)</Label><Input type="number" value={form.discount_fixed} onChange={e => setForm({...form, discount_fixed: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Max Uses (empty = unlimited)</Label><Input value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} /></div>
                <div><Label>Expires At</Label><Input type="date" value={form.expires_at} onChange={e => setForm({...form, expires_at: e.target.value})} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label>Active</Label></div>
              <Button onClick={handleSave} className="w-full">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPromoCodes;
